import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Stack,
    Card,
    CardContent,
    CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import axios from "axios";

// Styled Paper
const StyledPaper = styled(Paper)(({ theme, mode, customColors }) => ({
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    backgroundColor:
        mode === "dark"
            ? "#2A2A3C"
            : mode === "custom"
                ? customColors?.background || "#f5f5f5"
                : "#f0f7ff",
    borderRadius: "8px",
    boxShadow:
        mode === "dark"
            ? "0 2px 8px rgba(0,0,0,0.6)"
            : "0 2px 8px rgba(0,0,0,0.15)",
}));

// Styled Card
const StyledCard = styled(Card)(({ theme, mode, customColors }) => ({
    backgroundColor:
        mode === "dark"
            ? "#2A2A3C"
            : mode === "custom"
                ? customColors?.cardBg || "#fff"
                : "#fff",
    borderRadius: "12px",
    boxShadow:
        mode === "dark"
            ? "0 2px 10px rgba(0,0,0,0.6)"
            : mode === "custom"
                ? "0 2px 8px rgba(0,0,0,0.3)"
                : "0 2px 10px rgba(0,0,0,0.1)",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
        backgroundColor:
            mode === "dark"
                ? "#383C54"
                : mode === "custom"
                    ? customColors?.background || "#e8f4ff"
                    : "#f5f5f5",
    },
}));

// Palette setup
const palette = {
    light: {
        headerBg: "#e3f2fd",
        headerText: "#37474f",
        rowHover: "#f5f5f5",
        text: "#333",
    },
    dark: {
        headerBg: "#2A2A3C",
        headerText: "#EDEDED",
        rowHover: "#383C54",
        text: "#EDEDED",
    },
    custom: (customColors) => ({
        headerBg: customColors?.cardBg || "#f5f5f5",
        headerText: customColors?.text || "#111111",
        rowHover: customColors?.primary || customColors?.primary || "#e8f4ff",
        text: customColors?.text || "#111111",
    }),
};

export default function MyDetailsPanel({
    mode,
    customColors,
    setSnackbar,
    isLoading,
    setIsLoading,
    currentUserRole,
    customRolesDropdown,
}) {
    // ========== STATE ==========
    const [userDetails, setUserDetails] = useState({
        email: "",
        first_name: "",
        last_name: "",
        role: "",
        date_joined: "",
    });

    const apiUrl = process.env.REACT_APP_BASE_URL;

    // ========== EFFECTS ==========
    // Fetch current user details on component mount
    useEffect(() => {
        const fetchUserDetails = async () => {
            setIsLoading(true);
            try {
                const token = sessionStorage.getItem("authToken");
                const response = await axios.get(`${apiUrl}/api/users/me/`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUserDetails({
                    email: response.data.email || "",
                    first_name: response.data.first_name || "",
                    last_name: response.data.last_name || "",
                    role: response.data.role || "",
                    date_joined: response.data.date_joined || "",
                });
            } catch (error) {
                console.error("Failed to fetch user details:", error);
                setSnackbar({
                    open: true,
                    message: error.response?.data?.message || "Failed to fetch user details. Please try again.",
                    severity: "error",
                });
                setUserDetails({
                    email: "",
                    first_name: "",
                    last_name: "",
                    role: "",
                    date_joined: "",
                });
            } finally {
                setIsLoading(false);
            }
        };
        fetchUserDetails();
    }, [setIsLoading, setSnackbar]);

    // ========== RENDER ==========
    return (
        <>
            <StyledPaper
                sx={{
                    backgroundColor: mode === "dark"
                        ? palette.dark.headerBg
                        : mode === "custom"
                            ? customColors?.bgNav || "#f5f5f5"
                            : palette.light.headerBg,
                    p: 3,
                    borderRadius: "16px",
                    boxShadow: mode === "dark"
                        ? "0 8px 24px rgba(0, 0, 0, 0.3)"
                        : "0 8px 24px rgba(30, 139, 175, 0.12)",
                }}
            >
                {/* Header */}
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        mb: 3,
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        pb: 2,
                        borderBottom: mode === "dark"
                            ? "2px solid rgba(255, 255, 255, 0.1)"
                            : "2px solid rgba(30, 139, 175, 0.15)",
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                            fontWeight: 700,
                            letterSpacing: "-0.5px",
                        }}
                    >
                        My Details
                    </Typography>
                </Stack>

                {/* Loading State */}
                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress
                            sx={{
                                color: "#1e8bafff",
                            }}
                            size={48}
                        />
                    </Box>
                ) : userDetails.email ? (
                    <StyledCard
                        mode={mode}
                        customColors={customColors}
                        sx={{
                            background: mode === "dark"
                                ? "linear-gradient(135deg, #2A2A3C 0%, #1a1a2e 100%)"
                                : mode === "custom"
                                    ? `linear-gradient(135deg, ${customColors?.cardBg || "#ffffff"} 0%, ${customColors?.primary || "#f0f7ff"} 100%)`
                                    : "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)",
                            borderRadius: "16px",
                            boxShadow: mode === "dark"
                                ? "0 8px 24px rgba(0, 0, 0, 0.3)"
                                : "0 8px 24px rgba(30, 139, 175, 0.1)",
                            border: mode === "dark"
                                ? "1px solid rgba(255, 255, 255, 0.1)"
                                : "1px solid rgba(30, 139, 175, 0.1)",
                            transition: "all 0.3s ease",
                            "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: mode === "dark"
                                    ? "0 12px 32px rgba(0, 0, 0, 0.4)"
                                    : "0 12px 32px rgba(30, 139, 175, 0.2)",
                            },
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            {/* Details Grid */}
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                    gap: 3,
                                }}
                            >
                                {/* Email */}
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <EmailIcon
                                        sx={{
                                            color: "#1e8bafff",
                                            mr: 1.5,
                                            fontSize: 20,
                                        }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: mode === "dark"
                                                ? palette.dark.text
                                                : mode === "custom"
                                                    ? customColors?.text || "#111111"
                                                    : "#37474f",
                                            fontWeight: 500,
                                        }}
                                    >
                                        <strong>Email:</strong> {userDetails.email}
                                    </Typography>
                                </Box>

                                {/* First Name */}
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <PersonIcon
                                        sx={{
                                            color: "#1e8bafff",
                                            mr: 1.5,
                                            fontSize: 20,
                                        }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: mode === "dark"
                                                ? palette.dark.text
                                                : mode === "custom"
                                                    ? customColors?.text || "#111111"
                                                    : "#37474f",
                                            fontWeight: 500,
                                        }}
                                    >
                                        <strong>First Name:</strong> {userDetails.first_name || "Not provided"}
                                    </Typography>
                                </Box>

                                {/* Last Name */}
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <PersonIcon
                                        sx={{
                                            color: "#1e8bafff",
                                            mr: 1.5,
                                            fontSize: 20,
                                        }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: mode === "dark"
                                                ? palette.dark.text
                                                : mode === "custom"
                                                    ? customColors?.text || "#111111"
                                                    : "#37474f",
                                            fontWeight: 500,
                                        }}
                                    >
                                        <strong>Last Name:</strong> {userDetails.last_name || "Not provided"}
                                    </Typography>
                                </Box>

                                {/* Role */}
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <GroupIcon
                                        sx={{
                                            color: "#1e8bafff",
                                            mr: 1.5,
                                            fontSize: 20,
                                        }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: mode === "dark"
                                                ? palette.dark.text
                                                : mode === "custom"
                                                    ? customColors?.text || "#111111"
                                                    : "#37474f",
                                            fontWeight: 500,
                                        }}
                                    >
                                        <strong>
                                            {customRolesDropdown.some((role) => role.name === currentUserRole)
                                                ? "Custom Role"
                                                : "Role"}
                                            :
                                        </strong>{" "}
                                        {userDetails.role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "Not provided"}
                                    </Typography>
                                </Box>

                                {/* Date Joined */}
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <CalendarTodayIcon
                                        sx={{
                                            color: "#1e8bafff",
                                            mr: 1.5,
                                            fontSize: 20,
                                        }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: mode === "dark"
                                                ? palette.dark.text
                                                : mode === "custom"
                                                    ? customColors?.text || "#111111"
                                                    : "#37474f",
                                            fontWeight: 500,
                                        }}
                                    >
                                        <strong>Date Joined:</strong>{" "}
                                        {userDetails.date_joined
                                            ? new Date(userDetails.date_joined).toLocaleDateString("en-GB", {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                            })
                                            : "Not provided"}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </StyledCard>
                ) : (
                    <Box
                        sx={{
                            textAlign: "center",
                            py: 8,
                            opacity: 0.7,
                        }}
                    >
                        <PersonOffIcon sx={{ fontSize: 64, color: "#1e8bafff", mb: 2, opacity: 0.5 }} />
                        <Typography
                            variant="h6"
                            sx={{
                                color: mode === "dark"
                                    ? palette.dark.text
                                    : "#37474f",
                                fontWeight: 500,
                            }}
                        >
                            No user details available
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: mode === "dark"
                                    ? "rgba(255, 255, 255, 0.5)"
                                    : "rgba(0, 0, 0, 0.5)",
                                mt: 1,
                            }}
                        >
                            Your details could not be loaded.
                        </Typography>
                    </Box>
                )}
            </StyledPaper>
        </>
    );
}
